// Cookie consent banner — a bottom anchored, non blocking bar.
//
// Deliberately not a modal: there is no scrim, no scroll lock and no focus
// trap, so a visitor can read, scroll and navigate the whole site while the bar
// is up. Memory-only website analytics may run before a choice; persistent
// analytics and advertising require opt-in (see scripts/consent.js).
//
// Shown until the visitor makes an explicit choice; after that it is reachable
// again from the footer's "Cookie preferences" link, which calls the global
// window.openCookiePreferences().

import React from 'react';
import '../cookie-consent.css';
import { t } from './i18n/useT.js';
import { localizedHref } from './i18n/bootstrap.js';
import {
  OPTIONAL_CATEGORIES,
  acceptAll,
  clearAnalyticsStorage,
  clearMarketingStorage,
  hasGlobalPrivacyControl,
  getConsent,
  hasDecision,
  isWithdrawal,
  rejectAll,
  saveConsent,
} from './consent.js';

function CategoryRow({ id, checked, disabled, lockedLabel, onChange }) {
  const inputId = `sw-cookie-cat-${id}`;
  return (
    <div className={`sw-cookie-cat ${disabled ? 'is-locked' : ''}`}>
      <div className="sw-cookie-cat-main">
        <label className="sw-cookie-cat-name" htmlFor={inputId}>
          {t(`cookies.categories.${id}.name`)}
        </label>
        <p className="sw-cookie-cat-desc">{t(`cookies.categories.${id}.desc`)}</p>
      </div>
      {disabled ? (
        <span className="sw-cookie-cat-always">{lockedLabel || t('cookies.alwaysOn')}</span>
      ) : (
        <input
          id={inputId}
          className="sw-cookie-cat-input"
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
      )}
    </div>
  );
}

export function CookieConsentRoot() {
  // 'hidden' until a choice is needed, 'bar' for the one line ask, 'prefs' for
  // the per category view.
  const [view, setView] = React.useState(() => (hasDecision() ? 'hidden' : 'bar'));
  const [prefs, setPrefs] = React.useState(() => {
    const stored = getConsent();
    return {
      analytics: stored ? stored.analytics : false,
      marketing: stored ? stored.marketing : false,
    };
  });
  const headingRef = React.useRef(null);
  const focusWanted = React.useRef(false);

  React.useEffect(() => {
    window.openCookiePreferences = () => {
      const stored = getConsent();
      setPrefs({
        analytics: stored ? stored.analytics : false,
        marketing: stored ? stored.marketing : false,
      });
      focusWanted.current = true;
      setView('prefs');
    };
    return () => { window.openCookiePreferences = null; };
  }, []);

  // Only move focus when the panel was opened on purpose from the footer. The
  // first visit banner appears unannounced and must not steal focus from the page.
  React.useEffect(() => {
    if (view === 'prefs' && focusWanted.current) {
      focusWanted.current = false;
      headingRef.current?.focus();
    }
  }, [view]);

  // Escape closes the panel only once a decision exists. Before that, dismissing
  // without answering would look like consent, so the bar stays put.
  React.useEffect(() => {
    if (view === 'hidden') return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && hasDecision()) setView('hidden');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [view]);

  const commit = React.useCallback((write) => {
    const previous = getConsent();
    const next = write();
    // An already running tag cannot be unloaded, so withdrawing a category
    // clears what we can reach and reloads into a clean page.
    if (isWithdrawal(previous, next)) {
      clearAnalyticsStorage();
      clearMarketingStorage();
      window.location.reload();
      return;
    }
    setView('hidden');
  }, []);

  if (view === 'hidden') return null;

  const isPrefs = view === 'prefs';

  return (
    <div className="sw-cookie-layer">
      <div
        className={`sw-cookie ${isPrefs ? 'is-prefs' : ''}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby="sw-cookie-title"
        aria-label={isPrefs ? t('cookies.prefsTitle') : t('cookies.title')}
        aria-describedby="sw-cookie-text"
      >
        <div className="sw-cookie-copy">
          <h2 className="sw-cookie-title" id="sw-cookie-title" tabIndex={-1} ref={headingRef}>
            {/* Two renderings of the bar title, swapped by the compact phone
                CSS. The short span is aria-hidden so the dialog's accessible
                name stays the full title on every screen size. */}
            {isPrefs ? t('cookies.prefsTitle') : (
              <>
                <span className="sw-cookie-title-full">{t('cookies.title')}</span>
                <span className="sw-cookie-title-short" aria-hidden="true">{t('cookies.titleShort')}</span>
              </>
            )}
          </h2>
          <p className="sw-cookie-text" id="sw-cookie-text">
            {/* Wrapped so the compact phone layout can hide the explanation
                while keeping the policy link visible (cookie-consent.css). */}
            <span className="sw-cookie-body">{isPrefs ? t('cookies.prefsIntro') : t('cookies.body')}</span>{' '}
            <a href={`${localizedHref('/privacy')}#analytics`}>{t('cookies.policyLink')}</a>
          </p>
        </div>

        {isPrefs ? (
          <div className="sw-cookie-cats">
            <CategoryRow id="essential" checked disabled />
            {OPTIONAL_CATEGORIES.map((id) => (
              <CategoryRow
                key={id}
                id={id}
                checked={prefs[id]}
                disabled={id === 'marketing' && hasGlobalPrivacyControl()}
                lockedLabel={id === 'marketing' && hasGlobalPrivacyControl() ? t('cookies.gpcDisabled') : undefined}
                onChange={(value) => setPrefs((p) => ({ ...p, [id]: value }))}
              />
            ))}
          </div>
        ) : null}

        <div className="sw-cookie-actions">
          {isPrefs ? (
            <button
              type="button"
              className="sw-cookie-btn sw-cookie-btn--quiet"
              onClick={() => commit(() => saveConsent(prefs))}
            >
              {t('cookies.save')}
            </button>
          ) : (
            <button
              type="button"
              className="sw-cookie-btn sw-cookie-btn--quiet"
              onClick={() => setView('prefs')}
            >
              {t('cookies.manage')}
            </button>
          )}
          {/* Reject is the same size and weight as accept on purpose: refusing
              must cost exactly one click, like accepting. */}
          <button
            type="button"
            className="sw-cookie-btn sw-cookie-btn--quiet"
            onClick={() => commit(rejectAll)}
          >
            {t('cookies.rejectAll')}
          </button>
          <button
            type="button"
            className="sw-cookie-btn sw-cookie-btn--primary"
            onClick={() => commit(acceptAll)}
          >
            {t('cookies.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
}

if (typeof window !== 'undefined') window.CookieConsentRoot = CookieConsentRoot;
